import React from 'react';
import DateFormat from '../../lib.r2/utils/DateFormat';

// Device and evaluation metadata delivered by the backend once a flight has
// been performed and processed. All fields are nullable — older orders may
// never receive them (backfill), unprocessed orders not yet.
// Note: the backend also sends droneCameras, but that is the dock's internal
// "indoor" camera, not the capture camera — deliberately not displayed;
// sensorModel is the capture camera/sensor.
export interface FlightDeviceMetadata {
	flightHubTaskBeginAt?: string | null;
	droneModelName?: string | null;
	droneSerialNumber?: string | null;
	dockModelName?: string | null;
	dockSerialNumber?: string | null;
	gsdCm?: number | null;
	coordinateSystem?: string | null;
	geoidModel?: string | null;
	calibratedImages?: string | null;
	imageCount?: number | null;
	sensorModel?: string | null;
}

interface FlightDetailsSectionProps {
	order: FlightDeviceMetadata;
}

const hasText = (value?: string | null): value is string =>
	!!value && value.trim() !== '';

const hasNumber = (value?: number | null): value is number =>
	value !== null && value !== undefined && !Number.isNaN(value);

const DetailItem = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
	<div className="mb-4">
		<h3 className="text-base font-semibold p-0 m-0">{label}</h3>
		<div className="text-base">{value}</div>
		{hint && <div className="text-sm text-[#666666]">{hint}</div>}
	</div>
);

// "Flugdetails" (drone/dock) and "Auswertung" (Pix4D datasheet) blocks styled
// like the "Buchungsdetails" section on the order pages. Each block is hidden
// entirely while the order has no data for it.
export default function FlightDetailsSection({ order }: FlightDetailsSectionProps) {
	const {
		flightHubTaskBeginAt, droneModelName, dockModelName,
		gsdCm, coordinateSystem, geoidModel, calibratedImages, imageCount, sensorModel,
	} = order;

	const hasFlightDate = hasText(flightHubTaskBeginAt);
	const hasDrone = hasText(droneModelName);
	const hasDock = hasText(dockModelName);
	const hasEvaluation =
		hasNumber(gsdCm) || hasText(coordinateSystem) || hasText(geoidModel) ||
		hasText(calibratedImages) || hasNumber(imageCount) || hasText(sensorModel);

	if (!hasFlightDate && !hasDrone && !hasDock && !hasEvaluation) return null;

	return (
		<>
			{(hasFlightDate || hasDrone || hasDock) && (
				<div className="mt-8 pt-6 border-t border-[#333]">
					<h2 className="text-2xl mb-4 font-normal">Flugdetails</h2>

					{hasFlightDate && (
						<DetailItem
							label="Durchführungsdatum"
							value={DateFormat(flightHubTaskBeginAt as string, 'dd.mm.yyyy')}
						/>
					)}

					{hasDrone && (
						<DetailItem label="Drohne" value={droneModelName as string} />
					)}

					{hasDock && (
						<DetailItem label="Dock" value={dockModelName as string} />
					)}
				</div>
			)}

			{hasEvaluation && (
				<div className="mt-8 pt-6 border-t border-[#333]">
					<h2 className="text-2xl mb-4 font-normal">Auswertung</h2>

					{hasNumber(gsdCm) && (
						<DetailItem
							label="Bodenauflösung (GSD)"
							value={`${gsdCm.toLocaleString('de-AT', { maximumFractionDigits: 2 })} cm/px`}
						/>
					)}
					{hasText(coordinateSystem) && (
						<DetailItem label="Koordinatensystem" value={coordinateSystem} />
					)}
					{hasText(geoidModel) && (
						<DetailItem label="Geoid-Modell" value={geoidModel} />
					)}
					{hasNumber(imageCount) && (
						<DetailItem
							label="Bildanzahl"
							value={`${imageCount.toLocaleString('de-AT')} Bilder`}
							hint={hasText(calibratedImages) ? `Kalibriert: ${calibratedImages}` : undefined}
						/>
					)}
					{!hasNumber(imageCount) && hasText(calibratedImages) && (
						<DetailItem label="Kalibrierte Bilder" value={calibratedImages} />
					)}
					{hasText(sensorModel) && (
						<DetailItem label="Kamera / Sensor" value={sensorModel} />
					)}
				</div>
			)}
		</>
	);
}
